import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  Image,
  ScrollView,
  ActivityIndicator,
  StyleSheet,
  Linking,
  Picker,
  Pressable,
  Platform,
  useWindowDimensions,
} from 'react-native';
import { useForm, Controller } from 'react-hook-form';
import { Feather } from '@expo/vector-icons';
import { useNavigation, useFocusEffect } from "@react-navigation/native";
import { collection, getDocs, query, where } from "firebase/firestore";
import { firestore } from '../../services/firebase';
import {
  createCheckout,
  getCheckoutPaymentUrl,
  getOfferById,
  getPaymentFractions,
  getProductById
} from "../AdminPanel/Products/service/product-service";
import { getCEPData } from "./service/cep";
import Button from "../../components/Button";
import BrandInput from "../../components/BrandInput";
import THEME from '../../config/theme';
import { useCustomTheme } from "../../contexts/useCustomTheme";
import { useAuthUser } from "../../contexts/useAuthUser";
import { useCustomImages } from "../../api/Brand";
import { resolveTermsOfUseUrl } from "../../utils/termsOfUse";
import { removeSansSerif } from "../../utils/validators";
import { hexToRgba } from "../../utils/colorUtils";
import {
  onlyDigits,
  formatCpf,
  isValidCpf,
  formatPhone,
  isValidPhone,
  formatCep,
  formatCardNumber,
  formatExpiration,
  validateExpiration,
  EMAIL_PATTERN,
  formatBRL,
  buildCheckoutPrefill,
  buildCheckoutPayload,
} from "../../utils/checkoutForm";

const CEP_HELP_URL = 'https://buscacepinter.correios.com.br/app/endereco/index.php';
const NO_PARAMS = {};

const getFrequencyText = (frequency) =>
  !frequency || String(frequency) === '1' ? 'por mês' : `a cada ${frequency} meses`;

const getError = (errors, name) => name.split('.').reduce((acc, key) => acc?.[key], errors)?.message;

// ── Blocos do formulário ─────────────────────────────────────────────────────
// Declarados FORA da tela de propósito: componentes criados dentro do render
// são recriados a cada digitação e os campos perdem o foco.

const Section = ({ t, title, subtitle, children }) => (
  <View style={[styles.card, { backgroundColor: t.surface, borderColor: t.border }]}>
    <Text style={[styles.sectionTitle, { color: t.text, fontFamily: t.fontTitle }]}>{title}</Text>
    {!!subtitle && (
      <Text style={[styles.sectionSubtitle, { color: t.muted, fontFamily: t.fontText }]}>{subtitle}</Text>
    )}
    <View style={styles.sectionBody}>{children}</View>
  </View>
);

const Field = ({ t, label, error, hint, style, children }) => (
  <View style={[styles.field, style]}>
    <Text style={[styles.label, { color: t.text, fontFamily: t.fontText }]}>{label}</Text>
    {children}
    {!!error && <Text style={[styles.errorText, { fontFamily: t.fontText }]}>{error}</Text>}
    {!error && hint}
  </View>
);

const TextField = ({ t, control, errors, name, label, rules, format, onBlurExtra, hint, style, ...inputProps }) => {
  const error = getError(errors, name);
  return (
    <Field t={t} label={label} error={error} hint={hint} style={style}>
      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({ field: { onChange, onBlur, value } }) => (
          <BrandInput
            value={value}
            onChangeText={(text) => onChange(format ? format(text) : text)}
            onBlur={() => {
              onBlur();
              onBlurExtra && onBlurExtra(value);
            }}
            error={!!error}
            {...inputProps}
          />
        )}
      />
    </Field>
  );
};

const InfoLine = ({ t, icon, children }) => (
  <View style={styles.infoLine}>
    <Feather name={icon} size={16} color={t.muted} style={styles.infoIcon} />
    <Text style={[styles.infoText, { color: t.text, fontFamily: t.fontText }]}>{children}</Text>
  </View>
);

const SupportInfo = ({ t, supportContact, termsUrl }) => {
  if (!supportContact && !termsUrl) return null;
  return (
    <View style={styles.supportBlock}>
      {!!supportContact && <InfoLine t={t} icon="message-circle">Suporte: {supportContact}</InfoLine>}
      {!!termsUrl && (
        <Pressable onPress={() => Linking.openURL(termsUrl)}>
          <Text style={[styles.termsLink, { color: t.muted, fontFamily: t.fontText }]}>
            Termos de uso e política de privacidade
          </Text>
        </Pressable>
      )}
    </View>
  );
};

const OrderSummary = ({ t, product, supportContact, termsUrl, showSupport }) => {
  const isPlan = product.type === 'plan';
  return (
    <View style={[styles.card, { backgroundColor: t.surface, borderColor: t.border }]}>
      <Text style={[styles.overline, { color: t.muted, fontFamily: t.fontText }]}>Resumo do pedido</Text>
      <Text style={[styles.productName, { color: t.text, fontFamily: t.fontTitle }]}>{product.name}</Text>
      <View style={[styles.badge, { backgroundColor: t.accentSoft }]}>
        <Text style={[styles.badgeText, { color: t.text, fontFamily: t.fontText }]}>
          {isPlan ? 'Assinatura' : 'Compra única'}
        </Text>
      </View>

      <View style={styles.priceRow}>
        <Text style={[styles.price, { color: t.text, fontFamily: t.fontTitle }]}>{formatBRL(product.price)}</Text>
        {isPlan && (
          <Text style={[styles.priceSuffix, { color: t.muted, fontFamily: t.fontText }]}>
            {getFrequencyText(product.frequency)}
          </Text>
        )}
      </View>

      {!!product.description && (
        <Text style={[styles.description, { color: t.muted, fontFamily: t.fontText }]}>{product.description}</Text>
      )}

      <View style={[styles.divider, { backgroundColor: t.border }]} />
      {isPlan && !!product.trialDays && (
        <InfoLine t={t} icon="gift">{product.trialDays} dias grátis para testar</InfoLine>
      )}
      {!!product.expirationDate && (
        <InfoLine t={t} icon="calendar">Acesso por {product.expirationDate} dias após a compra</InfoLine>
      )}
      <InfoLine t={t} icon="credit-card">
        {isPlan ? 'Cobrança recorrente no cartão de crédito' : 'Cartão de crédito, Pix ou boleto'}
      </InfoLine>
      <InfoLine t={t} icon="shield">Pagamento em ambiente seguro</InfoLine>

      {showSupport && <SupportInfo t={t} supportContact={supportContact} termsUrl={termsUrl} />}
    </View>
  );
};

// ── Tela ─────────────────────────────────────────────────────────────────────

const Checkout = ({ route }) => {
  const navigation = useNavigation();
  const params = route.params || NO_PARAMS;
  const { ProductId, OfferId } = params;
  const { control, handleSubmit, setValue, getValues, formState: { errors } } = useForm();
  const { customColor, customFont, customInfo } = useCustomTheme();
  const { user, authUser, isLogged } = useAuthUser();
  const { data: logo } = useCustomImages('logoApp');
  const { width } = useWindowDimensions();
  const isDesktop = width >= 960;
  const isNarrow = width < 480;

  // Mesma resolução usada no rodapé e na tela de termos: aceita o domínio
  // digitado sem "https://" no painel.
  const clientTermsUrl = resolveTermsOfUseUrl(customInfo);
  const [productData, setProductData] = useState(null);
  const [appInfo, setAppInfo] = useState(null);
  const [installments, setInstallments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [submitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const prefilledFields = useRef(new Set());
  const lastCep = useRef(null);

  const isPlan = productData?.type === 'plan';

  // Pré-preenchimento: cadastro da aluna logada > parâmetros da vitrine na URL.
  // Logada, o e-mail fica travado: o acesso é liberado para o e-mail da compra.
  const prefill = useMemo(
    () => buildCheckoutPrefill({
      user: isLogged ? user : null,
      authEmail: isLogged ? authUser?.email : null,
      params,
    }),
    [isLogged, user, authUser, params]
  );
  const lockedEmail = isLogged && prefill.email ? prefill.email : null;

  useFocusEffect(
    React.useCallback(() => {
      // Redefine o estado de submitting para false quando retorna do PostCheckoutProduct
      setIsSubmitting(false);
    }, [])
  );

  useEffect(() => {
    if (!ProductId) return;

    const fetchAdminInfo = async () => {
      try {
        const q = query(collection(firestore, "users"), where("isAdmin", "==", true));
        const snapShot = await getDocs(q);
        snapShot.forEach(doc => {
          const admin = doc.data();
          if (admin.appInformation) {
            setAppInfo(admin.appInformation);
          }
        });
      } catch (error) {
        console.error('Erro ao buscar informações do admin:', error);
      }
    };

    fetchAdminInfo();

    const request = OfferId ? getOfferById(ProductId, OfferId) : getProductById(ProductId);
    request
      .then(res => setProductData({ productId: ProductId, ...res.data() }))
      .catch(error => console.error('Erro ao carregar produto:', error))
      .finally(() => setIsLoading(false));
  }, [ProductId, OfferId]);

  useEffect(() => {
    if (isPlan) {
      setValue('payment.paymentFraction', 1);
      getPaymentFractions({ price: productData.price, paymentFractions: productData.paymentFractions })
        .then(res => {
          setInstallments(
            res.data.map(installment => ({
              number: installment.number,
              total: installment.total / 100
            }))
          );
        });
    }
  }, [isPlan, productData, setValue]);

  // Preenche cada campo UMA vez e só se estiver vazio: nunca sobrescreve o que
  // a aluna digitou, nem repõe um campo que ela apagou para trocar o valor.
  useEffect(() => {
    if (isLoading) return;
    const fill = (name, value) => {
      if (!value || prefilledFields.current.has(name)) return;
      prefilledFields.current.add(name);
      if (!getValues(name)) setValue(name, value);
    };
    fill('customer.name', prefill.name);
    fill('customer.email', prefill.email);
    if (!lockedEmail) fill('customer.emailConfirm', prefill.email);
    fill('customer.document', prefill.document);
    fill('customer.phone', prefill.phone);
  }, [isLoading, prefill, lockedEmail, getValues, setValue]);

  const lookupCep = (zipCode) => {
    const cep = onlyDigits(zipCode);
    if (cep.length !== 8 || cep === lastCep.current) return;
    lastCep.current = cep;
    getCEPData(cep)
      .then(res => res.json())
      .then((json) => {
        if (json && !json.erro) {
          const fill = { shouldValidate: true };
          if (json.logradouro) setValue('address.address', json.logradouro, fill);
          if (json.bairro) setValue('address.neighborhood', json.bairro, fill);
          if (json.localidade) setValue('address.city', json.localidade, fill);
          if (json.uf) setValue('address.state', json.uf, fill);
        }
      })
      .catch((error) => {
        console.error('Erro ao buscar CEP:', error);
      });
  };

  const onSubmit = async (values) => {
    setIsSubmitting(true);
    setSubmitError(null);

    const formValues = {
      customer: { ...values.customer, ...(lockedEmail && { email: lockedEmail }) },
      ...(isPlan && {
        address: values.address,
        payment: { ...values.payment, paymentType: 'credit_card' },
      }),
    };
    const data = buildCheckoutPayload(formValues);

    if (isPlan) {
      navigation.navigate('PostCheckoutProduct', { productData: productData, data: data });
      return;
    }

    // Compra única: gera o link de pagamento e vai direto para o checkout da
    // Pagar.me. Na web, navega na MESMA aba — abrir nova aba depois de uma
    // chamada assíncrona é barrado pelo bloqueador de pop-up.
    try {
      const url = getCheckoutPaymentUrl(await createCheckout({ ...productData, ...data }));
      if (!url) throw new Error('Link de pagamento não retornado');

      if (Platform.OS === 'web') {
        window.location.assign(url);
      } else {
        await Linking.openURL(url);
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Erro ao iniciar pagamento:', error);
      setSubmitError('Não foi possível iniciar o pagamento. Tente novamente ou entre em contato com o suporte.');
      setIsSubmitting(false);
    }
  };

  const onInvalid = () => setSubmitError('Confira os campos destacados acima.');

  // ── Tokens da marca do cliente (mesmo padrão das telas do aluno) ──────────
  const clientText = customColor?.text?.app || THEME.COLORS.TEXT_MAIN;
  const clientPrimary = customColor?.primary || THEME.COLORS.PRIMARY_800;
  const t = {
    bg: customColor?.background?.app || THEME.COLORS.BACKGROUND_MAIN,
    text: clientText,
    primary: clientPrimary,
    accentSoft: hexToRgba(clientPrimary, 0.16) || '#F3F4F6',
    surface: hexToRgba(clientText, 0.04) || '#FFFFFF',
    surfaceStrong: hexToRgba(clientText, 0.08) || '#F3F4F6',
    border: hexToRgba(clientText, 0.14) || '#E5E7EB',
    inputBorder: hexToRgba(clientText, 0.22) || '#E5E7EB',
    muted: hexToRgba(clientText, 0.65) || '#6B7280',
    btnBg: customColor?.button?.backgroundApp || THEME.COLORS.PRIMARY_800,
    btnText: customColor?.button?.textApp || THEME.COLORS.TEXT_BUTTON,
    fontTitle: customFont?.fontFamilySubtitle
      ? removeSansSerif(customFont.fontFamilySubtitle)
      : THEME.FONTFAMILY.MEDIUM,
    fontText: customFont?.fontFamilyText
      ? removeSansSerif(customFont.fontFamilyText)
      : THEME.FONTFAMILY.REGULAR,
  };
  const fieldProps = { t, control, errors };
  const pagePadding = isNarrow ? 16 : 24;

  if (isLoading) {
    return (
      <View style={[styles.centered, { backgroundColor: t.bg }]}>
        <ActivityIndicator size="large" color={t.primary} />
      </View>
    );
  }

  if (!productData?.name) {
    return (
      <View style={[styles.centered, { backgroundColor: t.bg, padding: pagePadding }]}>
        <Feather name="alert-circle" size={32} color={t.muted} />
        <Text style={[styles.sectionTitle, { color: t.text, fontFamily: t.fontTitle, marginTop: 12 }]}>
          Produto não encontrado
        </Text>
        <Text style={[styles.sectionSubtitle, { color: t.muted, fontFamily: t.fontText, textAlign: 'center' }]}>
          Confira o link de compra ou fale com o suporte.
        </Text>
      </View>
    );
  }

  const summary = (
    <OrderSummary
      t={t}
      product={productData}
      supportContact={appInfo?.supportContact}
      termsUrl={clientTermsUrl}
      showSupport={isDesktop}
    />
  );

  const form = (
    <>
      <Section
        t={t}
        title="Seus dados"
        subtitle={lockedEmail
          ? 'Preenchemos com os dados da sua conta. Confira antes de continuar.'
          : 'O acesso ao conteúdo será liberado para este e-mail.'}
      >
        <TextField
          {...fieldProps}
          name="customer.name"
          label="Nome completo"
          placeholder="Como no seu documento"
          autoComplete="name"
          rules={{ validate: (v) => !!String(v || '').trim() || 'Informe seu nome completo.' }}
        />

        {lockedEmail ? (
          <Field
            t={t}
            label="E-mail"
            hint={
              <Text style={[styles.hint, { color: t.muted, fontFamily: t.fontText }]}>
                Compra vinculada à sua conta.
              </Text>
            }
          >
            <View style={[styles.readonlyBox, { backgroundColor: t.surfaceStrong, borderColor: t.inputBorder }]}>
              <Text numberOfLines={1} style={[styles.readonlyText, { color: t.text, fontFamily: t.fontText }]}>
                {lockedEmail}
              </Text>
              <Feather name="lock" size={14} color={t.muted} />
            </View>
          </Field>
        ) : (
          <View style={styles.row}>
            <TextField
              {...fieldProps}
              style={styles.col}
              name="customer.email"
              label="E-mail"
              placeholder="seuemail@exemplo.com"
              keyboardType="email-address"
              autoCapitalize="none"
              autoComplete="email"
              rules={{
                required: 'Informe seu e-mail.',
                validate: (v) => EMAIL_PATTERN.test(String(v || '').trim()) || 'E-mail inválido.',
              }}
            />
            <TextField
              {...fieldProps}
              style={styles.col}
              name="customer.emailConfirm"
              label="Confirme o e-mail"
              placeholder="Digite o e-mail novamente"
              keyboardType="email-address"
              autoCapitalize="none"
              rules={{
                required: 'Confirme seu e-mail.',
                validate: (v) =>
                  String(v || '').trim().toLowerCase() === String(getValues('customer.email') || '').trim().toLowerCase()
                  || 'Os e-mails não são iguais.',
              }}
            />
          </View>
        )}

        <View style={styles.row}>
          <TextField
            {...fieldProps}
            style={styles.col}
            name="customer.document"
            label="CPF"
            placeholder="000.000.000-00"
            keyboardType="number-pad"
            format={formatCpf}
            rules={{
              required: 'Informe seu CPF.',
              validate: (v) => isValidCpf(v) || 'CPF inválido.',
            }}
          />
          <TextField
            {...fieldProps}
            style={styles.col}
            name="customer.phone"
            label="Celular com DDD"
            placeholder="(00) 00000-0000"
            keyboardType="phone-pad"
            autoComplete="tel"
            format={formatPhone}
            rules={{
              required: 'Informe seu celular.',
              validate: (v) => isValidPhone(v) || 'Celular inválido.',
            }}
          />
        </View>
      </Section>

      {isPlan && (
        <Section t={t} title="Endereço de cobrança" subtitle="Usado pela operadora do cartão.">
          <TextField
            {...fieldProps}
            style={styles.cepField}
            name="address.zipCode"
            label="CEP"
            placeholder="00000-000"
            keyboardType="number-pad"
            autoComplete="postal-code"
            format={(v) => {
              const masked = formatCep(v);
              if (onlyDigits(masked).length === 8) lookupCep(masked);
              return masked;
            }}
            onBlurExtra={lookupCep}
            rules={{ validate: (v) => onlyDigits(v).length === 8 || 'Informe um CEP válido.' }}
            hint={
              <Pressable onPress={() => Linking.openURL(CEP_HELP_URL)}>
                <Text style={[styles.hintLink, { color: t.muted, fontFamily: t.fontText }]}>Não sei meu CEP</Text>
              </Pressable>
            }
          />
          <TextField
            {...fieldProps}
            name="address.address"
            label="Endereço"
            placeholder="Rua, avenida..."
            autoComplete="address-line1"
            rules={{ required: 'Informe o endereço.' }}
          />
          <View style={styles.row}>
            <TextField
              {...fieldProps}
              style={styles.colSmall}
              name="address.number"
              label="Número"
              rules={{ required: 'Informe o número.' }}
            />
            <TextField
              {...fieldProps}
              style={styles.col}
              name="address.complement"
              label="Complemento (opcional)"
              placeholder="Apto, bloco..."
            />
          </View>
          <View style={styles.row}>
            <TextField
              {...fieldProps}
              style={styles.col}
              name="address.neighborhood"
              label="Bairro"
              rules={{ required: 'Informe o bairro.' }}
            />
            <TextField
              {...fieldProps}
              style={styles.col}
              name="address.city"
              label="Cidade"
              rules={{ required: 'Informe a cidade.' }}
            />
            <TextField
              {...fieldProps}
              style={styles.colTiny}
              name="address.state"
              label="UF"
              placeholder="SP"
              autoCapitalize="characters"
              format={(v) => String(v || '').replace(/[^a-zA-Z]/g, '').slice(0, 2).toUpperCase()}
              rules={{ validate: (v) => (!v ? 'Informe a UF.' : /^[A-Z]{2}$/.test(v) || 'UF inválida.') }}
            />
          </View>
        </Section>
      )}

      {isPlan && (
        <Section t={t} title="Pagamento">
          <View style={[styles.methodChip, { borderColor: t.primary, backgroundColor: t.accentSoft }]}>
            <Feather name="credit-card" size={18} color={t.text} />
            <Text style={[styles.methodText, { color: t.text, fontFamily: t.fontTitle }]}>Cartão de crédito</Text>
            <Feather name="check-circle" size={18} color={t.primary} />
          </View>

          <TextField
            {...fieldProps}
            name="payment.cardNumber"
            label="Número do cartão"
            placeholder="0000 0000 0000 0000"
            keyboardType="number-pad"
            autoComplete="cc-number"
            format={formatCardNumber}
            rules={{
              required: 'Informe o número do cartão.',
              validate: (v) => (onlyDigits(v).length >= 13 && onlyDigits(v).length <= 19) || 'Número do cartão inválido.',
            }}
          />
          <TextField
            {...fieldProps}
            name="payment.holderName"
            label="Nome impresso no cartão"
            placeholder="Como aparece no cartão"
            autoCapitalize="characters"
            autoComplete="cc-name"
            rules={{ validate: (v) => !!String(v || '').trim() || 'Informe o nome impresso no cartão.' }}
          />
          <View style={styles.row}>
            <TextField
              {...fieldProps}
              style={styles.colSmall}
              name="payment.expirationDate"
              label="Validade"
              placeholder="MM/AA"
              keyboardType="number-pad"
              autoComplete="cc-exp"
              format={formatExpiration}
              rules={{ validate: (v) => (!v ? 'Informe a validade.' : validateExpiration(v)) }}
            />
            <TextField
              {...fieldProps}
              style={styles.colSmall}
              name="payment.cvv"
              label="CVV"
              placeholder="123"
              keyboardType="number-pad"
              autoComplete="cc-csc"
              format={(v) => onlyDigits(v).slice(0, 4)}
              rules={{ validate: (v) => (!v ? 'Informe o CVV.' : /^\d{3,4}$/.test(v) || 'CVV inválido.') }}
            />
          </View>

          <Field t={t} label="Parcelas" error={getError(errors, 'payment.paymentFraction')}>
            <Controller
              control={control}
              name="payment.paymentFraction"
              rules={{ required: 'Selecione o número de parcelas.' }}
              render={({ field: { onChange, value } }) => (
                <Picker
                  selectedValue={value}
                  onValueChange={onChange}
                  style={[styles.picker, { borderColor: t.inputBorder, color: t.text, fontFamily: t.fontText }]}
                >
                  {installments.map(({ number, total }) => (
                    <Picker.Item
                      key={number}
                      value={number}
                      label={number === 1
                        ? `1x de ${formatBRL(total)} (à vista)`
                        : `${number}x de ${formatBRL(total / number)} (total ${formatBRL(total)})`}
                    />
                  ))}
                </Picker>
              )}
            />
          </Field>
        </Section>
      )}

      <View style={styles.submitArea}>
        <Button
          title={isPlan ? 'Assinar agora' : 'Continuar para o pagamento'}
          onPress={handleSubmit(onSubmit, onInvalid)}
          isLoading={submitting}
          width="100%"
          borderRadius="10px"
          colorbutton={t.btnBg}
          colortitle={t.btnText}
        />
        {!!submitError && (
          <Text style={[styles.submitError, { fontFamily: t.fontText }]}>{submitError}</Text>
        )}
        <View style={styles.secureNote}>
          <Feather name="lock" size={13} color={t.muted} />
          <Text style={[styles.secureText, { color: t.muted, fontFamily: t.fontText }]}>
            {isPlan
              ? 'Pagamento processado em ambiente seguro.'
              : 'Na próxima etapa você escolhe cartão, Pix ou boleto, em ambiente seguro.'}
          </Text>
        </View>
      </View>
    </>
  );

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: t.bg }}
      contentContainerStyle={[styles.scrollContent, { padding: pagePadding }]}
      keyboardShouldPersistTaps="handled"
    >
      <View style={styles.page}>
        <View style={styles.header}>
          {logo?.url ? (
            <Image source={{ uri: logo.url }} style={styles.logo} resizeMode="contain" />
          ) : (
            <View />
          )}
          <View style={[styles.secureTag, { borderColor: t.border }]}>
            <Feather name="lock" size={13} color={t.muted} />
            <Text style={[styles.secureTagText, { color: t.muted, fontFamily: t.fontText }]}>Compra segura</Text>
          </View>
        </View>

        {isDesktop ? (
          <View style={styles.columns}>
            <View style={styles.mainColumn}>{form}</View>
            <View style={styles.sideColumn}>{summary}</View>
          </View>
        ) : (
          <>
            {summary}
            {form}
            <SupportInfo t={t} supportContact={appInfo?.supportContact} termsUrl={clientTermsUrl} />
          </>
        )}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  centered: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  scrollContent: {
    flexGrow: 1,
    alignItems: 'center',
  },
  page: {
    width: '100%',
    maxWidth: 1080,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
    gap: 12,
  },
  logo: {
    width: 140,
    height: 48,
  },
  secureTag: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderWidth: 1,
    borderRadius: 999,
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  secureTagText: {
    fontSize: 13,
  },
  columns: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 24,
  },
  mainColumn: {
    flex: 1,
    minWidth: 0,
  },
  sideColumn: {
    width: 360,
  },
  card: {
    borderWidth: 1,
    borderRadius: 14,
    padding: 20,
    marginBottom: 16,
    boxShadow: '0 1px 2px rgba(16,24,40,0.06)',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
  },
  sectionSubtitle: {
    fontSize: 13,
    marginTop: 4,
  },
  sectionBody: {
    marginTop: 16,
  },
  row: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    columnGap: 12,
  },
  // flexBasis + flexGrow: lado a lado quando cabe, empilha no celular.
  col: {
    flexGrow: 1,
    flexBasis: 200,
  },
  colSmall: {
    flexGrow: 1,
    flexBasis: 120,
  },
  colTiny: {
    flexGrow: 0,
    flexBasis: 72,
  },
  cepField: {
    maxWidth: 240,
  },
  field: {
    marginBottom: 14,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 6,
  },
  hint: {
    fontSize: 12.5,
    marginTop: 6,
  },
  hintLink: {
    fontSize: 12.5,
    marginTop: 6,
    textDecorationLine: 'underline',
  },
  errorText: {
    fontSize: 12.5,
    color: THEME.COLORS.ALERT,
    marginTop: 6,
  },
  // Mesma geometria do BrandInput (44 / raio 10 / borda 1 / padding 14).
  readonlyBox: {
    minHeight: 44,
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  readonlyText: {
    fontSize: 15,
    flexShrink: 1,
  },
  picker: {
    height: 44,
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 10,
    fontSize: 15,
    backgroundColor: 'transparent',
  },
  methodChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderWidth: 1.5,
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 14,
    marginBottom: 16,
  },
  methodText: {
    flex: 1,
    fontSize: 15,
    fontWeight: '600',
  },
  submitArea: {
    marginBottom: 16,
  },
  submitError: {
    fontSize: 13,
    color: THEME.COLORS.ALERT,
    textAlign: 'center',
    marginTop: 10,
  },
  secureNote: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    marginTop: 12,
  },
  secureText: {
    fontSize: 12.5,
    textAlign: 'center',
    flexShrink: 1,
  },
  overline: {
    fontSize: 12,
    fontWeight: '600',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },
  productName: {
    fontSize: 20,
    fontWeight: '700',
    marginTop: 6,
  },
  badge: {
    alignSelf: 'flex-start',
    borderRadius: 999,
    paddingVertical: 4,
    paddingHorizontal: 10,
    marginTop: 10,
  },
  badgeText: {
    fontSize: 12.5,
    fontWeight: '600',
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 14,
  },
  price: {
    fontSize: 28,
    fontWeight: '700',
  },
  priceSuffix: {
    fontSize: 15,
  },
  description: {
    fontSize: 14,
    lineHeight: 20,
    marginTop: 8,
  },
  divider: {
    height: 1,
    marginVertical: 16,
  },
  infoLine: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 10,
  },
  infoIcon: {
    marginTop: 2,
    marginRight: 10,
  },
  infoText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
  },
  supportBlock: {
    marginTop: 6,
    paddingTop: 6,
  },
  termsLink: {
    fontSize: 13,
    textDecorationLine: 'underline',
    marginTop: 2,
  },
});

export default Checkout;
